import type { ApiClient } from "@/api/client";
import type { DecidedProposal, DecisionResult, LevelChange, LevelsMatrix, Proposal, AttachmentUpload, ExploreHop, ExploreKind, GraphProjects, ReadinessIndex, ActivationResult, AgentCatalog, BudgetInput, BudgetOverview, SkillDetail, SkillDrafted, SkillInput, SkillReviewed, SkillSummary, AgentDefinition, AgentDetail, AgentSummary, ChatAsked, ChatList, ChatPage, ConversationSummary, DraftSaved, ConversationView, CreatedGitHubApp, GitHubAppManifest, JiraOnboarding, TeamNote, TeamNoteInput, TraceView, TurnRequest, TurnStarted } from "@/api/types";
import type { AbRunResult, AbVariant, Activity, Artifact, Brief, ArtifactName, BlastResult, Config, EventsPage, FlowDetail, FlowSummary, Footprint, GitHubOnboarding, GraphNode, GraphSnapshot, JiraRepoMapping, RepoReadiness, JobView, Lineage, LineageDismissal, LineageTrace, Me, Alert, NodeDetail, RunContext, RunImpact, RunRecord, Schedule, Workspace } from "@/api/types";

// The compare flow path — the only per-flow constant left in the client, used by
// the runCompare back-compat wrapper.
export const COMPARE_FLOW_PATH = "f/jira_bug_analysis_fix_compare/main";

// ── me ──
export async function fetchMe(c: ApiClient): Promise<Me> {
  return (await c.get<{ me: Me }>("/me")).me;
}

// ── flows (discovery + schema) ──
export async function listFlows(c: ApiClient): Promise<FlowSummary[]> {
  return (await c.get<{ flows: FlowSummary[] }>("/flows")).flows;
}
export async function getFlowSchema(c: ApiClient, flowPath: string): Promise<FlowDetail> {
  // Query param, not a path segment — flow paths contain "/" and API Gateway
  // mangles encoded slashes in path parameters. Response wraps the flow.
  return (await c.get<{ flow: FlowDetail }>(`/flow?path=${encodeURIComponent(flowPath)}`)).flow;
}

// ── configs ──
export async function listConfigs(c: ApiClient, flowPath?: string): Promise<Config[]> {
  const q = flowPath ? `?flow_path=${encodeURIComponent(flowPath)}` : "";
  return (await c.get<{ configs: Config[] }>(`/configs${q}`)).configs;
}
export async function createConfig(
  c: ApiClient,
  body: { flow_path: string; name: string; description?: string; params: Record<string, unknown> },
): Promise<Config> {
  return (await c.post<{ config: Config }>("/configs", body)).config;
}
export async function updateConfig(
  c: ApiClient,
  id: string,
  body: { flow_path: string; name: string; description?: string; params: Record<string, unknown> },
): Promise<Config> {
  return (await c.put<{ config: Config }>(`/configs/${encodeURIComponent(id)}`, body)).config;
}
export async function deleteConfig(c: ApiClient, id: string): Promise<void> {
  await c.del<unknown>(`/configs/${encodeURIComponent(id)}`);
}

// ── mappings ──
export async function listMappings(c: ApiClient): Promise<JiraRepoMapping[]> {
  return (await c.get<{ mappings: JiraRepoMapping[] }>("/mappings")).mappings;
}
export async function upsertMapping(
  c: ApiClient, projectKey: string, body: Partial<JiraRepoMapping>,
): Promise<JiraRepoMapping> {
  return (await c.put<{ mapping: JiraRepoMapping }>(`/mappings/${encodeURIComponent(projectKey)}`, body)).mapping;
}
export async function deleteMapping(c: ApiClient, projectKey: string): Promise<void> {
  await c.del<unknown>(`/mappings/${encodeURIComponent(projectKey)}`);
}

// ── onboarding (ADR-005) ──
export async function getGitHubOnboarding(c: ApiClient): Promise<GitHubOnboarding> {
  return c.get<GitHubOnboarding>("/onboarding/github");
}
/** Asks GitHub what the app is installed on, then answers like getGitHubOnboarding. */
export async function getJiraOnboarding(c: ApiClient): Promise<JiraOnboarding> {
  return c.get<JiraOnboarding>("/onboarding/jira");
}
/** Create the console's Jira webhook, or bring it in step with the projects that have @majlis on. */
export async function connectJira(c: ApiClient): Promise<JiraOnboarding> {
  return c.post<JiraOnboarding>("/onboarding/jira/connect", {});
}
export async function refreshGitHubOnboarding(c: ApiClient): Promise<GitHubOnboarding> {
  return c.post<GitHubOnboarding>("/onboarding/github/refresh", {});
}
export async function getRepoReadiness(c: ApiClient, repo: string): Promise<RepoReadiness> {
  return c.get<RepoReadiness>(`/onboarding/github/readiness?repo=${encodeURIComponent(repo)}`);
}
/** GitHub's form and the manifest that create the Majlis app (only while no live app exists). */
export async function getGitHubAppManifest(c: ApiClient, origin: string, state: string): Promise<GitHubAppManifest> {
  return c.get<GitHubAppManifest>(`/onboarding/github/app/manifest?origin=${encodeURIComponent(origin)}&state=${encodeURIComponent(state)}`);
}
/** GitHub's one-hour code into the app: the setup Lambda checks its owner and saves its credentials. */
export async function createGitHubApp(c: ApiClient, code: string): Promise<CreatedGitHubApp> {
  return c.post<CreatedGitHubApp>("/onboarding/github/app", { code });
}

// ── schedules ──
export async function listSchedules(c: ApiClient): Promise<Schedule[]> {
  return (await c.get<{ schedules: Schedule[] }>("/schedules")).schedules;
}
export async function createSchedule(
  c: ApiClient,
  body: {
    flow_path: string; name: string; cron: string;
    timezone?: string; config_id?: string; params: Record<string, unknown>;
  },
): Promise<{ path: string; cron: string }> {
  return await c.post("/schedules", body);
}
export async function setScheduleEnabled(c: ApiClient, name: string, enabled: boolean): Promise<void> {
  await c.patch(`/schedules/${encodeURIComponent(name)}`, { enabled });
}
export async function deleteSchedule(c: ApiClient, name: string): Promise<void> {
  await c.del<unknown>(`/schedules/${encodeURIComponent(name)}`);
}

// ── runs ──
export async function listRuns(c: ApiClient, limit = 50, ticket?: string): Promise<RunRecord[]> {
  const t = ticket ? `&ticket=${encodeURIComponent(ticket)}` : "";
  return (await c.get<{ runs: RunRecord[] }>(`/runs?limit=${limit}${t}`)).runs;
}
// Launch two attempts of the same flow together (A/B). Both get their own branch.
export async function runAb(
  c: ApiClient,
  body: { flow_path: string; config_id?: string; params: Record<string, unknown>; variants: AbVariant[] },
): Promise<AbRunResult> {
  return await c.post("/runs/ab", body);
}
export async function fetchEvents(
  c: ApiClient, jobId: string, cursor = "", opts: { signal?: AbortSignal } = {},
): Promise<EventsPage> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return await c.get<EventsPage>(`/runs/${encodeURIComponent(jobId)}/events${q}`, opts);
}
export async function fetchArtifact(c: ApiClient, jobId: string, name: ArtifactName): Promise<Artifact> {
  return await c.get<Artifact>(`/runs/${encodeURIComponent(jobId)}/artifacts/${encodeURIComponent(name)}`);
}
export async function cancelRun(c: ApiClient, jobId: string): Promise<void> {
  await c.post(`/runs/${encodeURIComponent(jobId)}/cancel`, {});
}
export async function runFlow(
  c: ApiClient, body: { flow_path: string; config_id?: string; params: Record<string, unknown> },
): Promise<RunRecord> {
  return await c.post("/runs", body);
}
/** A fix run waiting at its plan: approve it, and the same run implements the plan the planner wrote. */
export async function approvePlan(c: ApiClient, jobId: string): Promise<{ job_id: string; approved_by: string; approved_at: string }> {
  return await c.post(`/runs/${encodeURIComponent(jobId)}/approve`, {});
}
/** Or reject it: the run ends without implementing anything. */
export async function rejectPlan(c: ApiClient, jobId: string, reason: string): Promise<{ job_id: string; rejected_by: string }> {
  return await c.post(`/runs/${encodeURIComponent(jobId)}/reject`, { reason });
}
// Back-compat wrapper: delegates to runFlow with the compare path.
export async function runCompare(
  c: ApiClient, body: { config_id?: string; params: Record<string, unknown> },
): Promise<RunRecord> {
  return await runFlow(c, { flow_path: COMPARE_FLOW_PATH, ...body });
}
export async function fetchJob(c: ApiClient, jobId: string): Promise<JobView> {
  return (await c.get<{ job: JobView }>(`/runs/${encodeURIComponent(jobId)}`)).job;
}

// ── living code map ──
const enc = encodeURIComponent;
export async function listGraphProjects(c: ApiClient): Promise<GraphProjects> {
  return await c.get<GraphProjects>("/graph/projects");
}
export async function listWorkspaces(c: ApiClient): Promise<Workspace[]> {
  return (await c.get<{ workspaces: Workspace[] }>("/graph/workspaces")).workspaces;
}
export async function fetchGraph(c: ApiClient, ws: string, maxLevel = 3): Promise<GraphSnapshot> {
  return await c.get<GraphSnapshot>(`/graph/${enc(ws)}?max_level=${maxLevel}`);
}
// Node ids contain ":" and "/", so they travel as a query param, not a path segment.
export async function fetchNode(c: ApiClient, ws: string, id: string): Promise<NodeDetail> {
  return await c.get<NodeDetail>(`/graph/${enc(ws)}/node?id=${enc(id)}`);
}
export async function fetchBlast(c: ApiClient, ws: string, ids: string[], depth = 3): Promise<BlastResult> {
  return await c.get<BlastResult>(`/graph/${enc(ws)}/blast?ids=${ids.map(enc).join(",")}&depth=${depth}`);
}
export async function fetchFootprint(c: ApiClient, ws: string, ticket: string): Promise<Footprint> {
  return await c.get<Footprint>(`/graph/${enc(ws)}/footprint?ticket=${enc(ticket)}`);
}
export async function fetchImpact(c: ApiClient, jobId: string): Promise<RunImpact> {
  return await c.get<RunImpact>(`/runs/${enc(jobId)}/impact`);
}
export async function fetchContext(c: ApiClient, jobId: string): Promise<RunContext> {
  return await c.get<RunContext>(`/runs/${enc(jobId)}/context`);
}
export async function fetchActivity(c: ApiClient, ws: string, days: number): Promise<Activity> {
  return await c.get<Activity>(`/graph/${enc(ws)}/activity?days=${days}`);
}
export async function fetchBrief(c: ApiClient, days: number): Promise<Brief> {
  return await c.get<Brief>(`/brief?days=${days}`);
}
export async function searchGraph(c: ApiClient, ws: string, q: string): Promise<GraphNode[]> {
  return (await c.get<{ nodes: GraphNode[] }>(`/graph/${enc(ws)}/search?q=${enc(q)}`)).nodes;
}
export async function fetchLineage(c: ApiClient, ticket: string): Promise<Lineage> {
  return await c.get<Lineage>(`/tickets/${enc(ticket)}/lineage`);
}
export async function traceLineage(c: ApiClient, ticket: string): Promise<LineageTrace> {
  return await c.post<LineageTrace>(`/tickets/${enc(ticket)}/lineage`, {});
}
// Taking a wrong link out of a ticket's lineage, and putting it back (H-132).
export async function listDismissals(c: ApiClient, ticket: string): Promise<LineageDismissal[]> {
  return (await c.get<{ dismissals: LineageDismissal[] }>(`/tickets/${enc(ticket)}/lineage/dismissals`)).dismissals;
}
export async function dismissLink(c: ApiClient, ticket: string, target: string, reason?: string): Promise<LineageDismissal> {
  return await c.post<LineageDismissal>(`/tickets/${enc(ticket)}/lineage/dismissals`, reason ? { target, reason } : { target });
}
export async function restoreLink(c: ApiClient, ticket: string, id: string): Promise<void> {
  await c.del<unknown>(`/tickets/${enc(ticket)}/lineage/dismissals/${enc(id)}`);
}
export async function listAlerts(c: ApiClient): Promise<Alert[]> {
  // Without kinds=all the API lists schedule alerts only (what older consoles understand).
  return (await c.get<{ alerts: Alert[] }>("/alerts?kinds=all")).alerts;
}
export async function resolveAlert(c: ApiClient, id: string, reEnable: boolean): Promise<void> {
  await c.post(`/alerts/${enc(id)}/resolve`, { re_enable: reEnable });
}

// ── the supervisor chat (ADR-007) ──
export interface ChatQuestion { question: string; request_id: string; run?: string }
export async function listChats(c: ApiClient, ticket: string): Promise<ChatList> {
  return await c.get<ChatList>(`/tickets/${enc(ticket)}/chats`);
}
/** A new conversation (thread null) or a follow-up. A resent request_id is the same question. */
export async function askSupervisor(c: ApiClient, ticket: string, thread: string | null, q: ChatQuestion): Promise<ChatAsked> {
  return await c.post<ChatAsked>(thread ? `/chats/${enc(thread)}/turns` : `/tickets/${enc(ticket)}/chats`, q);
}
export async function pollChat(c: ApiClient, thread: string, after = 0, opts: { signal?: AbortSignal } = {}): Promise<ChatPage> {
  return await c.get<ChatPage>(`/chats/${enc(thread)}?after=${after}`, opts);
}
export async function deleteChat(c: ApiClient, thread: string): Promise<void> {
  await c.del<unknown>(`/chats/${enc(thread)}`);
}

// ── conversations with Majlis's front door (ADR-008 H-032) ──
export async function routingCandidates(c: ApiClient, projects: string[], team: string): Promise<import("@/api/types").RoutingCandidate[]> {
  const query = new URLSearchParams({ candidates: "1", projects: projects.join(","), ...(team ? { team } : {}) });
  return (await c.get<{ candidates: import("@/api/types").RoutingCandidate[] }>(`/conversations?${query}`)).candidates;
}
export async function listConversations(c: ApiClient): Promise<ConversationSummary[]> {
  return (await c.get<{ conversations: ConversationSummary[] }>("/conversations")).conversations;
}
// ── attachments (H-128): a pre-signed upload straight to S3, then the message names the id ──
export const ATTACH_MAX_FILES = 5;
export const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
const BY_EXTENSION: Record<string, string> = { txt: "text/plain", log: "text/plain", csv: "text/csv", tsv: "text/tab-separated-values",
  md: "text/markdown", json: "application/json", ndjson: "application/x-ndjson", jsonl: "application/x-ndjson", xml: "application/xml",
  yaml: "application/yaml", yml: "application/yaml", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" };
export const ATTACH_ACCEPT = Object.keys(BY_EXTENSION).map((e) => `.${e}`).join(",");
/** The type to declare for a file: the browser's when it's one we take, else by its extension. */
export function attachmentType(name: string, browserType: string): string | null {
  const known = new Set(Object.values(BY_EXTENSION));
  if (browserType && known.has(browserType)) return browserType;
  return BY_EXTENSION[name.split(".").pop()?.toLowerCase() ?? ""] ?? null;
}
export async function uploadAttachment(c: ApiClient, file: File): Promise<{ id: string; name: string }> {
  const type = attachmentType(file.name, file.type);
  if (!type) throw new Error(`${file.name}: attach text (logs, CSV, JSON, Markdown) or a PNG/JPEG screenshot`);
  if (file.size > ATTACH_MAX_BYTES) throw new Error(`${file.name} is over 10 MB`);
  const up = await c.post<AttachmentUpload>("/attachments", { name: file.name, type, size: file.size });
  if (!up.url.startsWith("mock:")) {
    const r = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
    if (!r.ok) throw new Error(`${file.name} didn't upload (${r.status})`);
  }
  return { id: up.id, name: up.name };
}
export async function startConversation(c: ApiClient, req: TurnRequest): Promise<TurnStarted> {
  return await c.post<TurnStarted>("/conversations", req);
}
export async function sendTurn(c: ApiClient, id: string, req: TurnRequest): Promise<TurnStarted> {
  return await c.post<TurnStarted>(`/conversations/${enc(id)}/turns`, req);
}
export async function fetchConversation(c: ApiClient, id: string, after = 0, opts: { signal?: AbortSignal } = {}): Promise<ConversationView> {
  return await c.get<ConversationView>(`/conversations/${enc(id)}?after=${after}`, opts);
}

// the run as steps a person can watch (H-159): the conversation's latest run, or one of its runs
export async function fetchTrace(c: ApiClient, id: string, runId?: string | null, opts: { signal?: AbortSignal } = {}): Promise<TraceView> {
  return await c.get<TraceView>(`/conversations/${enc(id)}/trace${runId ? `?run_id=${enc(runId)}` : ""}`, opts);
}

// ── team notes (ADR-008 H-088) ──
export async function listNotes(c: ApiClient, projects: string[]): Promise<{ notes: TeamNote[]; writable: Record<string, import("./types").NotesWritable> }> {
  const got = await c.get<{ notes: TeamNote[]; writable?: Record<string, import("./types").NotesWritable> }>(`/notes?projects=${enc(projects.join(","))}`);
  return { notes: got.notes, writable: got.writable ?? {} };
}
export async function saveNote(c: ApiClient, note: TeamNoteInput): Promise<TeamNote> {
  return (await c.put<{ note: TeamNote }>("/notes", note)).note;
}
export async function deleteNote(c: ApiClient, id: string): Promise<void> {
  await c.del<unknown>(`/notes/${enc(id)}`);
}

// ── agents and their versions (ADR-008 H-110) ──
export async function listAgents(c: ApiClient): Promise<AgentSummary[]> {
  return (await c.get<{ agents: AgentSummary[] }>("/agents")).agents;
}
export async function fetchAgent(c: ApiClient, name: string): Promise<AgentDetail> {
  return await c.get<AgentDetail>(`/agents/${enc(name)}`);
}
/** `team`: the workspace the definition is for; its own connections join the organisation's (H-153 step 3). */
export async function fetchAgentCatalog(c: ApiClient, team?: string | null): Promise<AgentCatalog> {
  return await c.get<AgentCatalog>(`/agents/catalog${team ? `?team=${enc(team)}` : ""}`);
}
export async function saveAgentDraft(c: ApiClient, name: string, definition: AgentDefinition, team?: string | null, rehearseBeforePublishing = false): Promise<DraftSaved> {
  return await c.post<DraftSaved>(`/agents/${enc(name)}/drafts`, { definition, ...(team ? { team } : {}), ...(rehearseBeforePublishing ? { rehearse_before_publishing: true } : {}) });
}
export async function activateAgentVersion(c: ApiClient, name: string, version: number): Promise<ActivationResult> {
  return await c.post<ActivationResult>(`/agents/${enc(name)}/versions/${version}/activate`);
}
/** Switch an agent off (new work refused everywhere, its running work cut off, its hub permissions withdrawn) or on
 * again (exactly as it was); its owners only, with a reason (2026-10-18). Asking for the state it's in changes nothing. */
export async function setAgentAvailability(c: ApiClient, name: string, enabled: boolean, reason: string): Promise<import("./types").AvailabilityResult> {
  return await c.put<import("./types").AvailabilityResult>(`/agents/${enc(name)}/availability`, { enabled, reason });
}
// Scheduled tasks (H-080; admins): runs as the team's service user.
export async function listAgentSchedules(c: ApiClient, name: string): Promise<import("./types").AgentSchedule[]> {
  return (await getAgentSchedules(c, name)).schedules;
}
/** An agent's schedules the reader manages, and the workspaces they may schedule it for (2026-10-20: a workspace's admins
 * schedule for their own workspace; organisation admins for any) */
export async function getAgentSchedules(c: ApiClient, name: string): Promise<{ schedules: import("./types").AgentSchedule[]; may_schedule_for?: string[] }> {
  return await c.get<{ schedules: import("./types").AgentSchedule[]; may_schedule_for?: string[] }>(`/agents/${enc(name)}/schedules`);
}
export async function createAgentSchedule(c: ApiClient, name: string, body: { name: string; expression: string; timezone: string;
  projects: string[]; message: string; team?: string }): Promise<import("./types").AgentSchedule> {
  return (await c.post<{ schedule: import("./types").AgentSchedule }>(`/agents/${enc(name)}/schedules`, body)).schedule;
}
export async function approveAgentSchedule(c: ApiClient, name: string, id: string): Promise<import("./types").AgentSchedule> {
  return (await c.post<{ schedule: import("./types").AgentSchedule }>(`/agents/${enc(name)}/schedules/${enc(id)}/approve`, {})).schedule;
}
export async function agentDirectory(c: ApiClient): Promise<import("./types").DirectoryAgent[]> {
  return (await c.get<{ agents: import("./types").DirectoryAgent[] }>("/agents/directory")).agents;
}
export async function boardAgents(c: ApiClient, project: string): Promise<import("./types").BoardAgents> {
  return c.get(`/projects/${enc(project)}/agents`);
}
export async function addBoardAgent(c: ApiClient, project: string, name: string): Promise<import("./types").BoardAgents> {
  return c.put(`/projects/${enc(project)}/agents/${enc(name)}`, {});
}
export async function removeBoardAgent(c: ApiClient, project: string, name: string): Promise<import("./types").BoardAgents> {
  return c.del(`/projects/${enc(project)}/agents/${enc(name)}`);
}
export async function listAgentLessons(c: ApiClient, name: string): Promise<{ lessons: import("./types").AgentLesson[]; candidates: import("./types").LessonCandidate[] }> {
  return c.get(`/agents/${enc(name)}/lessons`);
}
export async function proposeAgentLessons(c: ApiClient, name: string, feedbackIds?: string[]): Promise<import("./types").AgentLesson[]> {
  return (await c.post<{ lessons: import("./types").AgentLesson[] }>(`/agents/${enc(name)}/lessons/propose`,
    feedbackIds?.length ? { feedback_ids: feedbackIds } : {})).lessons;
}
export async function decideAgentLesson(c: ApiClient, name: string, id: string, body: { decision: "approve" | "reject" | "retire";
  statement?: string; kind?: string; projects?: string[]; widen?: boolean; expires_in_days?: number }): Promise<import("./types").AgentLesson> {
  return (await c.post<{ lesson: import("./types").AgentLesson }>(`/agents/${enc(name)}/lessons/${enc(id)}/decide`, body)).lesson;
}
export async function deleteAgentSchedule(c: ApiClient, name: string, id: string): Promise<void> {
  await c.del(`/agents/${enc(name)}/schedules/${enc(id)}`);
}
// "Investigate again" (H-093): re-run a finished specialist in your conversation with a note.
export async function investigateAgain(c: ApiClient, id: string, delegation: string, note: string)
  : Promise<{ delegation_id: string; agent_type: string; status: string; run_id: string }> {
  return await c.post(`/conversations/${enc(id)}/delegations/${enc(delegation)}/again`, { note });
}
export async function rateAnswer(c: ApiClient, id: string, seq: number, rating: "up" | "down", reason?: string | null)
  : Promise<{ seq: number; rating: string; reason: string | null }> {
  return await c.post(`/conversations/${enc(id)}/feedback`, { seq, rating, ...(reason ? { reason } : {}) });
}

// ── budgets (H-062; admins) ──
export async function listBudgets(c: ApiClient): Promise<BudgetOverview> {
  return c.get<BudgetOverview>("/budgets");
}
export async function saveBudget(c: ApiClient, b: BudgetInput): Promise<void> {
  await c.put<unknown>("/budgets", b);
}
export async function deleteBudget(c: ApiClient, id: string): Promise<void> {
  await c.del<unknown>(`/budgets/${enc(id)}`);
}
// Connector bindings (H-114): narrowing an agent's connector tools per team.
export async function listBindings(c: ApiClient): Promise<import("@/api/types").BindingMatrix> {
  return c.get<import("@/api/types").BindingMatrix>("/bindings");
}
export async function saveBinding(c: ApiClient, b: import("@/api/types").BindingInput): Promise<void> {
  await c.put<unknown>("/bindings", b);
}
export async function removeBinding(c: ApiClient, id: string): Promise<void> {
  await c.del<unknown>(`/bindings/${enc(id)}`);
}

// ── the skills store (H-087): admins and team admins draft, someone else reviews ──
export async function listSkills(c: ApiClient): Promise<SkillSummary[]> {
  return (await c.get<{ skills: SkillSummary[] }>("/skills")).skills;
}
export async function getSkill(c: ApiClient, name: string): Promise<SkillDetail> {
  return await c.get<SkillDetail>(`/skills/${enc(name)}`);
}
export async function draftSkill(c: ApiClient, s: SkillInput): Promise<SkillDrafted> {
  return await c.post<SkillDrafted>("/skills", s);
}
export async function reviewSkill(c: ApiClient, name: string, version: number): Promise<SkillReviewed> {
  return await c.post<SkillReviewed>(`/skills/${enc(name)}/versions/${version}/review`);
}

// ── agentic readiness of repositories (H-129) ──
// ── the lineage explorer (H-130) ──
export async function exploreLineage(c: ApiClient, kind: ExploreKind, id: string): Promise<ExploreHop> {
  return await c.get<ExploreHop>(`/lineage?kind=${enc(kind)}&id=${enc(id)}`);
}
export async function fetchReadiness(c: ApiClient, refresh = false): Promise<ReadinessIndex> {
  return await c.get<ReadinessIndex>(`/readiness${refresh ? "?refresh=1" : ""}`);
}

// ── proposals and decisions (H-065, H-060) ──
export async function listProposals(c: ApiClient): Promise<Proposal[]> {
  return (await c.get<{ proposals: Proposal[] }>("/proposals")).proposals;
}
export async function decideProposal(c: ApiClient, p: Proposal, verdict: "approve" | "reject", note?: string): Promise<DecisionResult> {
  return await c.post<DecisionResult>(`/proposals/${enc(p.id)}/decide`, { verdict, gate_version: p.gate_version, ...(note ? { note } : {}) });
}
export async function listDecisions(c: ApiClient, days = 30): Promise<DecidedProposal[]> {
  return (await c.get<{ decisions: DecidedProposal[] }>(`/decisions?days=${days}`)).decisions;
}
export async function fetchLevels(c: ApiClient): Promise<LevelsMatrix> {
  return await c.get<LevelsMatrix>("/policy/levels");
}
/** H-060: tighten one row (or clear it, level null); the hub's policy follows within minutes */
export async function setLevel(c: ApiClient, body: { agent: string; action: string; team: string | null; level: string | null }): Promise<LevelChange> {
  return await c.put<LevelChange>("/policy/levels", body);
}

// ── teams (ADR-008 H-034, H-137): admins only ──────────────────────────────────
// a workspace (a team): its people are listed to organisation admins only (2026-10-16)
export interface TeamRow { key: string; name: string; members?: { email: string; team_admin: boolean }[] }
export async function listTeams(c: ApiClient): Promise<TeamRow[]> {
  return (await c.get<{ teams: TeamRow[] }>("/teams")).teams;
}
export async function createTeam(c: ApiClient, key: string, name: string): Promise<{ key: string; name: string }> {
  return (await c.post<{ team: { key: string; name: string } }>("/teams", { key, name })).team;
}
/** 2026-10-18: someone not yet signed in is invited (`status: "invited"`); `slack` says whether RushdBot told them */
export interface MemberChange { status?: "added" | "updated" | "invited"; slack?: "sent" | "no_slack_user" | "failed" | "not_configured" }
export async function setTeamMember(c: ApiClient, key: string, email: string, teamAdmin: boolean): Promise<MemberChange> {
  return (await c.put<MemberChange>(`/teams/${enc(key)}/members/${enc(email)}`, { team_admin: teamAdmin })) ?? {};
}
/** 2026-10-18: an organisation admin renames a workspace (its key too); its people, agents and connections follow */
export async function renameTeam(c: ApiClient, key: string, newKey: string, name: string): Promise<{ key: string; name: string }> {
  return (await c.put<{ team: { key: string; name: string } }>(`/teams/${enc(key)}/name`, { key: newKey, name })).team;
}
export async function removeTeamMember(c: ApiClient, key: string, email: string): Promise<void> {
  await c.del(`/teams/${enc(key)}/members/${enc(email)}`);
}

export async function stopTeam(c: ApiClient, conversation: string, session: string): Promise<{ state: string }> {
  return await c.post(`/conversations/${enc(conversation)}/team/${enc(session)}/stop`, {});
}


export async function releaseBudgetHold(c: ApiClient, runId: string, reason: string): Promise<void> {
  await c.post<unknown>(`/budgets/holds/${enc(runId)}/release`, { reason });
}

export async function directTeam(c: ApiClient, conversation: string, session: string, action: "answer" | "steer", text: string, epoch: number): Promise<{ state: string }> {
  return await c.post(`/conversations/${enc(conversation)}/team/${enc(session)}/${action}`, { text, ...(action === "answer" ? { epoch } : {}) });
}

/** A ticket's title, type and status from Jira, for one Majlis hasn't run on yet (2026-10-21). */
export async function ticketSummary(c: ApiClient, key: string): Promise<{ key: string; summary: string; type: string | null; status: string | null }> {
  return (await c.get<{ ticket: { key: string; summary: string; type: string | null; status: string | null } }>(`/tickets/${enc(key)}/summary`)).ticket;
}

/** What agents summoned on a ticket by a Jira comment answered there, newest first (H-078). */
export async function ticketAnswers(c: ApiClient, key: string): Promise<import("./types").TicketAnswer[]> {
  return (await c.get<{ answers: import("./types").TicketAnswer[] }>(`/tickets/${enc(key)}/answers`)).answers;
}
export interface FixBrief {
  id: string; label: string; ticket: string; created_by: string; created_at: string;
  source_run_id: string; source_turn_id: string; source_turn_seq: number; hash: string;
  snapshot: import("./types").InvestigationOutput;
}
export interface FixBriefSelection {
  turn_id: string; hypotheses: { index: number; evidence: number[] }[]; could_not_check: number[]; what_changed: number[];
}
export async function createFixBrief(c: ApiClient, key: string, selection: FixBriefSelection): Promise<FixBrief> {
  return (await c.post<{ brief: FixBrief }>(`/tickets/${enc(key)}/fix-briefs`, selection)).brief;
}
export async function ticketFixBriefs(c: ApiClient, key: string): Promise<FixBrief[]> {
  return (await c.get<{ briefs: FixBrief[] }>(`/tickets/${enc(key)}/fix-briefs`)).briefs;
}
export async function readFixBrief(c: ApiClient, key: string, id: string): Promise<FixBrief> {
  return (await c.get<{ brief: FixBrief }>(`/tickets/${enc(key)}/fix-briefs/${enc(id)}`)).brief;
}
/** H-054's first slice: the ticket's fix runs on the harness, each plan revision with its decision, Publish's outcome. */
export interface HarnessFixRevision {
  proposal_id: string; revision: number; status: "pending" | "approved" | "rejected" | "expired" | "superseded" | string;
  gate_version: string; expires_at: string | null; plan: string | null;
  grant: { repo: string | null; base_branch: string | null; base_sha: string | null; max_files: number | null; plan_hash: string | null; fix_brief_id?: string | null; evidence_hash?: string | null };
  decision: { verdict: string; by: string | null; at: string; note: string | null } | null; can_decide: boolean;
}
export interface HarnessFix {
  run_id: string; status: string; asked_by: string | null; team: string | null; started_at: string | null; finished_at: string | null;
  cost_usd: number | null; repo: string | null; base_branch: string | null; base_sha: string | null; pr_url: string | null;
  revisions: HarnessFixRevision[]; fix_brief?: FixBrief | null;
  stages: { stage: string; attempt: number; status: string; model: string | null; cost_usd: number | null; error: string | null;
            started_at: string | null; finished_at: string | null }[];
  execution: { status: string; artifact_hash: string | null; error: string | null; finished_at: string | null } | null;
  refused: string | null;
}
export async function ticketHarnessFixes(c: ApiClient, key: string): Promise<HarnessFix[]> {
  return (await c.get<{ fixes: HarnessFix[] }>(`/tickets/${enc(key)}/harness-fixes`)).fixes;
}
export async function decideHarnessPlan(c: ApiClient, r: HarnessFixRevision, verdict: "approve" | "reject" | "request_changes", note?: string) {
  return await c.post<{ verdict: string; executed?: boolean; outcome?: string }>(`/proposals/${enc(r.proposal_id)}/decide`,
    { verdict, gate_version: r.gate_version, ...(note ? { note } : {}) });
}

export async function fetchAgentRehearsal(c: ApiClient, name: string, version: number, id = "latest") {
  return c.get<import("./types").AgentRehearsalView>(`/agents/${enc(name)}/versions/${version}/rehearsals/${enc(id)}`);
}
export async function runAgentRehearsal(c: ApiClient, name: string, version: number, body: { forbidden: string; own: string }) {
  return c.post<{ id: string; state: string }>(`/agents/${enc(name)}/versions/${version}/rehearsals`, body);
}
export async function publishAgent(c: ApiClient, name: string, version: number) {
  return c.post<{ state: "published"; version: number }>(`/agents/${enc(name)}/versions/${version}/publish`);
}
